import {
  createDepositReservation,
  createDepositWithError,
  notifyPaymentFailure,
} from '@features/booking/api/reservations';
import {
  createWlDepositStripe,
  createWlDepositWithError,
} from '@features/booking/api/waitingList';
import type { DepositGrabPayload } from '@features/booking/api/reservations';
import { getStripePromise } from '@features/booking/hooks/useStripeV3';
import { handle3dsChallenge } from '@features/booking/hooks/handle3dsChallenge';
import { getStripePublishableKeyForTokenAccount } from '@features/booking/utils/amexChange';
import { env } from '@core/config/env';
import * as Sentry from '@sentry/react';
import type { BaseReservationFields, CommitContext, CommitDerived } from './shared';
import { persistSuccessAndAdvance, buildDepositFields, fireConversion } from './shared';

/**
 * Stripe deposit/no-show reservation path — with 3DS support and a no_show
 * card-decline fallback.
 *
 * Key invariants (see plan §payment-branches §3 + §3DS-SNAPSHOT §2):
 * - Stripe pubkey resolution goes through getStripePublishableKeyForTokenAccount,
 *   which routes AMEX no_show through platform credentials when stripeTokenAccount
 *   is set. Sentry breadcrumb fires when AMEX + no_show + missing stripeTokenAccount.
 * - was3ds = !!response.requires_action; if 3DS fired, the card-decline fallback
 *   must NOT run (reservation in pending_payment; handle3dsChallenge already
 *   cancelled it via cancel-3ds-payment).
 * - threeDsResult.reservationId is the confirmed ID; response.reservationId is
 *   pre-3DS and unusable for state writes.
 * - Card-decline fallback only runs for no_show + non-3DS errors. The
 *   /deposit-grab-with-error handler skips payment (typeAction=traditional)
 *   and never reads card fields.
 */
export async function commitDepositStripe(
  ctx: CommitContext,
  derived: CommitDerived,
  basePayload: BaseReservationFields,
): Promise<void> {
  const { booking, restaurant } = ctx;
  const { isWaitingList, effectiveGuaranteeType } = derived;

  // Narrow types for the deposit endpoints. depositStripe is only reached with
  // effectiveGuaranteeType === 'deposit' | 'no_show' (caller-enforced).
  const guaranteeTypeNarrow = effectiveGuaranteeType as 'deposit' | 'no_show';
  const depositBase: DepositGrabPayload = {
    ...basePayload,
    date: basePayload.date as string,
    menu: basePayload.menu as number,
    ...buildDepositFields(ctx),
    type: guaranteeTypeNarrow,
    cardToken: booking.cardToken!,
    paymentMethod: booking.paymentMethod || 'unknown',
  };

  let was3ds = false;
  try {
    const response = isWaitingList
      ? await createWlDepositStripe(
          ctx.currentLanguage,
          { ...depositBase, date: booking.wlDates },
        )
      : await createDepositReservation(ctx.currentLanguage, depositBase);

    was3ds = !!response.requires_action;

    // Diagnose AMEX no_show flows that arrived without a resolved
    // stripeTokenAccount — they cannot route through the right Stripe key.
    // Sentry breadcrumb category: 'stripe-token-routing'. (Fires whether or not
    // 3DS triggers — the misconfiguration is about routing the pubkey, which
    // matters for both v3 challenges and non-3DS validations.)
    if (
      booking.stripeTokenAccount === null &&
      booking.paymentMethod === 'American Express' &&
      effectiveGuaranteeType === 'no_show'
    ) {
      Sentry.addBreadcrumb({
        category: 'stripe-token-routing',
        message: 'Missing stripeTokenAccount for AMEX no_show on deposit path',
        level: 'warning',
        data: {
          restaurant: restaurant.id,
          paymentMethod: booking.paymentMethod,
          guaranteeType: effectiveGuaranteeType,
        },
      });
    }
    const stripePublishableKey = getStripePublishableKeyForTokenAccount({
      tokenAccount: booking.stripeTokenAccount,
      restaurantPublishableKey: restaurant.publicKeyPayment,
      platformPublishableKey: env.stripeFallbackKey,
    });
    // T30 overlay: only fire on3dsStart/on3dsEnd when 3DS will actually
    // happen. handle3dsChallenge returns null for non-3DS responses without
    // any user-visible work; firing the callback unconditionally would flash
    // the overlay on plain card payments.
    if (was3ds) ctx.on3dsStart?.();
    let threeDsResult;
    try {
      threeDsResult = await handle3dsChallenge(
        response,
        getStripePromise(stripePublishableKey),
      );
    } finally {
      if (was3ds) ctx.on3dsEnd?.();
    }
    if (threeDsResult) {
      persistSuccessAndAdvance(
        ctx,
        derived,
        threeDsResult.reservationId,
        response.uniqueId,
        guaranteeTypeNarrow,
      );
      return;
    }

    persistSuccessAndAdvance(
      ctx,
      derived,
      response.reservationId,
      response.uniqueId,
      guaranteeTypeNarrow,
    );
  } catch (cardErr) {
    // If 3DS was involved, do NOT use the card decline fallback.
    // The reservation already exists in PENDING_PAYMENT state.
    if (was3ds) {
      throw cardErr;
    }
    // Card decline fallback for no_show only (pre-3DS errors).
    // The /deposit-grab-with-error handler skips payment processing
    // (typeAction=traditional) and never reads card fields, so we
    // reuse depositBase as-is instead of sending the Stripe token
    // in an encryptedCard field the backend would ignore.
    if (effectiveGuaranteeType === 'no_show' && booking.cardToken) {
      const response = isWaitingList
        ? await createWlDepositWithError(
            ctx.currentLanguage,
            { ...depositBase, date: booking.wlDates },
          )
        : await createDepositWithError(ctx.currentLanguage, depositBase);

      booking.setReservationResult(response.reservationId, response.uniqueId);
      booking.setGuestInfo(derived.guestName, derived.guestEmail, derived.guestPhone);

      await notifyPaymentFailure({
        restaurant: restaurant.id,
        error: cardErr instanceof Error ? cardErr.message : 'Card declined',
      }).catch(() => { /* non-blocking */ });

      // Note: fireConversion + invalidateTimeSlots + nav run AFTER
      // notifyPaymentFailure here (not via persistSuccessAndAdvance which
      // would invert the order). The original handleConfirm enforced this
      // sequence for the card-decline fallback specifically. Using
      // goToStep('BOOKING_FINAL') instead of goToNext() — same reasoning
      // as persistSuccessAndAdvance: explicit destination avoids the
      // step-set-invalidated-by-flushSync hazard.
      fireConversion(ctx, derived, response.reservationId, response.uniqueId, guaranteeTypeNarrow);
      ctx.invalidateTimeSlots();
      ctx.goToStep('BOOKING_FINAL');
    } else {
      throw cardErr;
    }
  }
}
